// Today's link paths, read for the new app (docs/architecture/clients.md,
// "Today's links"). Today's grammar is the link host's page and the Mac app's
// parser (links/link.js and macos/Sources/BoardLink.swift there):
//
//   /<key> /<id>  /issue/<key|id>  /link/<key|id>   an issue
//   /doc/<id>                                         a plan, or a document
//   /r/<id>   /run/<id>                               a request, a run
//   /docs  /docs/<path>  /p/<Key>/docs/<path>         the repository's docs
//   /p/<Key>/board|plans|code[/<id>]                  as they are here
//   /board /plans /code[/<id>]                        the selected project's
//   /projects /now /agents                            earlier names of pages
//
// `readOldLink` says where one leads now, from the board the store holds. The
// frame draws it as a page (OldLink.tsx); the workspace applies its
// redirects when a tab opens or is recovered (workspace/model.ts).
import type { Repository } from './movedDocuments'
import { isUuid, readKey, sameKey } from '../lib/keys'
import { movedProjectPath, planPath } from '../projects/routing'
import type { BoardState } from '../store/store'
import { MOVED_DOCUMENTS, type MovedDocuments } from './movedDocuments'

export type OldLinkState = Pick<BoardState, 'projects' | 'selectedProjectId' | 'plans' | 'planDetails' | 'issues' | 'questions' | 'entries'>

export type OldLink =
  /** The new address (replace), with the old fragment kept. */
  | { kind: 'redirect'; to: string }
  /** A document that is no longer in the app: the repository path it moved to. */
  | { kind: 'moved'; path: string; repository: Repository }
  /** Something today's board had and this one does not carry over. */
  | { kind: 'gone'; what: 'request' | 'run' }
  /** `/doc/<id>` that neither the map nor the snapshot knows: the server may know the plan. */
  | { kind: 'plan'; id: string }
  | { kind: 'file'; sha256: string }

function segments(pathname: string): string[] | null {
  // As the link host's page and the Mac app read it: decoded whole, then split.
  try { return decodeURIComponent(pathname).split('/').filter(Boolean) } catch { return null }
}

/** An issue by its key in any spelling, or its id: its address, or null when the board has no such issue. */
function issueAddress(ref: string, issues: OldLinkState['issues']): string | null {
  const id = ref.toLowerCase()
  for (const issue of issues.values()) if (issue.id.toLowerCase() === id || sameKey(issue.key, ref)) return `/${issue.key}`
  return null
}

/** A page of the selected project's (or first's), in its project's address; the unqualified one while there is none. */
function inProject(page: string, search: string, hash: string, state: OldLinkState): string {
  const path = `/${page}`
  return movedProjectPath(path, search, hash, [...state.projects.values()], state.selectedProjectId) ?? path + search + hash
}

/** Where today's address `pathname` leads now; null for an address that is the new app's own, or that no link is. */
export function readOldLink(pathname: string, search: string, hash: string, state: OldLinkState, moved: MovedDocuments = MOVED_DOCUMENTS): OldLink | null {
  const parts = segments(pathname)
  if (!parts?.length) return null
  const kind = parts[0]!.toLowerCase()
  const rest = parts.slice(1)
  const to = (address: string): OldLink => ({ kind: 'redirect', to: address })
  switch (kind) {
    case 'p': return null
    case 'issue':
    case 'link': {
      if (rest.length === 0) return kind === 'issue' ? to('/') : null
      if (rest.length !== 1) return null
      const address = issueAddress(rest[0]!, state.issues)
      if (address) return to(address + hash)
      // The board has no such issue: its page says so.
      return readKey(rest[0]!) ? to(`/${rest[0]}${hash}`) : isUuid(rest[0]!) ? to(`/${rest[0]!.toLowerCase()}${hash}`) : null
    }
    case 'doc': {
      if (rest.length !== 1) return null
      const id = rest[0]!.toLowerCase()
      const path = Object.hasOwn(moved, id) ? moved[id] : undefined
      if (path !== undefined) return { kind: 'moved', path, repository: 'next' }
      // An archived plan is not in the snapshot, but its page may have read it.
      const plan = state.plans.get(rest[0]!) ?? state.plans.get(id) ?? state.planDetails.get(rest[0]!) ?? state.planDetails.get(id)
      const project = plan && state.projects.get(plan.project)
      return project ? to(planPath(project.key, plan.id) + search + hash) : { kind: 'plan', id: isUuid(id) ? id : rest[0]! }
    }
    case 'r': {
      if (rest.length !== 1) return null
      const question = state.questions.get(rest[0]!) ?? state.questions.get(rest[0]!.toLowerCase())
      const issue = question?.issue ? state.issues.get(question.issue) : undefined
      return issue ? to(`/${issue.key}`) : { kind: 'gone', what: 'request' }
    }
    case 'files':
      return rest.length === 1 && /^[0-9a-f]{64}$/.test(rest[0]!) ? { kind: 'file', sha256: rest[0]! } : null
    case 'run': {
      if (rest.length !== 1) return null
      const source = `run:${rest[0]!.toLowerCase()}`
      for (const entry of state.entries.values()) {
        if (entry.import_source?.toLowerCase() !== source) continue
        const issue = state.issues.get(entry.issue)
        if (issue) return to(`/${issue.key}#entry-${entry.number}`)
      }
      return { kind: 'gone', what: 'run' }
    }
    case 'docs':
      return null
    case 'projects': return rest.length === 0 ? to(inProject('plans', search, hash, state)) : null
    case 'now': return rest.length === 0 ? to(inProject('board', '', hash, state)) : null
    case 'agents': return rest.length === 0 ? to('/settings/workspace/runners') : null
    default: {
      // An issue's key in another spelling, or its id: its key.
      if (rest.length !== 0) return null
      const address = issueAddress(parts[0]!, state.issues)
      return address && address !== `/${parts[0]}` ? to(address + search + hash) : null
    }
  }
}
